import React from 'react';
import PropTypes from 'prop-types';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import { Trans } from '@lingui/macro';
import styled from 'styled-components';

import { Button, WizardStep } from '@oca/ui';

import { OnChange } from '@lib/react-final-form-on-change';
import { createDocumentModel, DOCUMENTS } from '../model/static';

export function EmployeeEditForm({
  editable,
  initialValues,
  steps,
  validate,
  onSubmit,
  activeStepIndex = 0,
  moveToStep,
  onCancel,
}) {
  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      mutators={{ ...arrayMutators }}
      onSubmit={onSubmit}
      subscription={{ pristine: true, submitting: true }}
      render={({ handleSubmit, form, ...finalFormProps }) => (
        <form onSubmit={handleSubmit} noValidate>
          <OnTypeChange form={form} />
          {steps.map(({ component: StepForm }, index) => (
            <WizardStep key={index}>
              {({ isActive }) =>
                isActive && (
                  // Temporary: do not disable documents section,
                  // because it prevents from downloading file
                  <Fieldset disabled={editable === false && index !== 2}>
                    <StepForm
                      editable={editable}
                      form={form}
                      initialValues={initialValues}
                      {...finalFormProps}
                    />
                  </Fieldset>
                )
              }
            </WizardStep>
          ))}
          {editable && (
            <FormActionBar>
              {onCancel ? (
                <Button type="button" variant="secondary" onClick={onCancel}>
                  <Trans>Cancel</Trans>
                </Button>
              ) : (
                <div />
              )}
              <ActionGroup>
                {moveToStep && activeStepIndex > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => moveToStep(activeStepIndex - 1)}
                  >
                    &larr; <Trans>Previous</Trans>
                  </Button>
                )}
                {moveToStep && activeStepIndex < steps.length - 1 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => moveToStep(activeStepIndex + 1)}
                  >
                    <Trans>Next step</Trans> &rarr;
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  disabled={finalFormProps.submitting}
                >
                  {finalFormProps.submitting ? (
                    <Trans>Saving...</Trans>
                  ) : (
                    <Trans>Save changes</Trans>
                  )}
                </Button>
              </ActionGroup>
            </FormActionBar>
          )}
        </form>
      )}
    />
  );
}

const FormActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eaecf0;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

EmployeeEditForm.propTypes = {
  activeStepIndex: PropTypes.number,
  editable: PropTypes.bool.isRequired,
  initialValues: PropTypes.objectOf(PropTypes.any),
  moveToStep: PropTypes.func,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.func,
    }),
  ).isRequired,
  validate: PropTypes.func.isRequired,
};

EmployeeEditForm.defaultProps = {
  activeStepIndex: 0,
  initialValues: {},
  moveToStep: undefined,
  onCancel: undefined,
};

// eslint-disable-next-line react/prop-types
function OnTypeChange({ form }) {
  return (
    <OnChange name="isResident">
      {(value, prevValue) => {
        if (value === false && prevValue === true) {
          form.mutators.push('documents', createDocumentModel(DOCUMENTS.VISA));
          form.mutators.push(
            'documents',
            createDocumentModel(DOCUMENTS.WORK_PERM),
          );
        } else if (value === true && prevValue === false) {
          form.mutators.pop('documents');
          form.mutators.pop('documents');
        }
      }}
    </OnChange>
  );
}

const Fieldset = styled.div`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.9 : 1)};
  padding: 0;
  margin: 0;
  border: none;
`;
